// Lottie preview / import panel - loads .json animations and shows preview.
import React, { useRef, useState } from "react";
import Lottie from "lottie-react";
import { Upload, Play, Pause } from "lucide-react";

const SAMPLE = {
  v: "5.5.7",
  fr: 30,
  ip: 0,
  op: 60,
  w: 200,
  h: 200,
  nm: "demo",
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: "dot",
      sr: 1,
      ks: {
        o: { a: 0, k: 100 },
        r: {
          a: 1,
          k: [
            { t: 0, s: [0], h: 0, i: { x: [0.5], y: [1] }, o: { x: [0.5], y: [0] } },
            { t: 60, s: [360] },
          ],
        },
        p: { a: 0, k: [100, 100, 0] },
        a: { a: 0, k: [0, 0, 0] },
        s: { a: 0, k: [100, 100, 100] },
      },
      ao: 0,
      shapes: [
        {
          ty: "gr",
          it: [
            { d: 1, ty: "el", s: { a: 0, k: [80, 80] }, p: { a: 0, k: [0, 0] } },
            { ty: "fl", c: { a: 0, k: [1, 0.42, 0, 1] }, o: { a: 0, k: 100 }, r: 1 },
            { ty: "tr", p: { a: 0, k: [0, 0] }, a: { a: 0, k: [0, 0] }, s: { a: 0, k: [100, 100] }, r: { a: 0, k: 0 }, o: { a: 0, k: 100 } },
          ],
        },
      ],
      ip: 0,
      op: 60,
      st: 0,
      bm: 0,
    },
  ],
};

export default function LottiePanel() {
  const [data, setData] = useState(SAMPLE);
  const [playing, setPlaying] = useState(true);
  const [error, setError] = useState("");
  const ref = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target.result);
        if (!json.v || !json.layers) throw new Error("Not a Lottie JSON");
        setData(json);
      } catch (err) {
        setError(err.message || "Invalid JSON");
      }
    };
    reader.readAsText(f);
  };

  const toggle = () => {
    setPlaying((p) => {
      const next = !p;
      if (next) ref.current?.play();
      else ref.current?.pause();
      return next;
    });
  };

  return (
    <div className="space-y-2" data-testid="lottie-panel">
      <div className="lottie-preview h-40 border border-line flex items-center justify-center overflow-hidden">
        <Lottie
          lottieRef={ref}
          animationData={data}
          loop
          autoplay={playing}
          style={{ height: 140, width: 140 }}
        />
      </div>
      <div className="flex gap-1.5">
        <label className="flex-1 flex items-center justify-center gap-1.5 border border-line hover:border-[#FF6B00] hover:text-ink text-ink3 py-1.5 px-2 text-[10px] font-mono uppercase tracking-wider cursor-pointer">
          <Upload className="h-3 w-3" />
          Load .json
          <input type="file" accept="application/json" onChange={onFile} className="hidden" data-testid="lottie-upload-input" />
        </label>
        <button
          onClick={toggle}
          data-testid="lottie-play-toggle-btn"
          className="border border-line hover:border-[#FF6B00] hover:text-ink text-ink3 py-1.5 px-2.5"
        >
          {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
        </button>
      </div>
      {error && <p className="text-[10px] text-red-400 font-mono">{error}</p>}
      <p className="text-[10px] text-ink4 font-mono leading-tight">
        Preview any LottieFiles JSON. Embed in your project by linking lottie-web.
      </p>
    </div>
  );
}
